import React, { useState, useEffect, useCallback, useRef } from 'react';
import OutputsAccountTable from './OutputsAccountTable';
import ForecastDropdown from "./ForecastDropdown";
import ForecastPeriodDropdown from "./ForecastPeriodDropdown";
import Box from "@mui/material/Box";
import CustomerDropdown from "./CustomerDropdown";
import ProductFiltersButton from "./ProductFiltersButton";
import ProductFilters from "./ProductFilters";
import axiosInstance from "../AxiosInstance";
import IconButton from "@mui/material/IconButton";
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import CircularProgress from '@mui/material/CircularProgress';
import Tooltip from '@mui/material/Tooltip';
import { Skeleton, Paper, Grid, Tabs, Tab } from "@mui/material";
import Switch from '@mui/material/Switch';
import FormControlLabel from '@mui/material/FormControlLabel';
import { downloadCsv } from "../Utilities/csvUtility";

const ForecastOutputsAccount = () => {
    const [forecasts, setForecasts] = useState([]);
    const [aggregationPeriod, setAggregationPeriod] = useState("monthly");
    const [selectedForecastId, setSelectedForecastId] = useState(null);
    const [selectedForecastPeriodId, setSelectedForecastPeriodId] = useState(null);
    const [selectedCustomerId, setSelectedCustomerId] = useState(null);
    const [selectedChannelId, setSelectedChannelId] = useState(null);
    const [selectedBrands, setSelectedBrands] = useState([]);
    const [selectedProductGroups, setSelectedProductGroups] = useState([]);
    const [selectedProductIds, setSelectedProductIds] = useState([]);
    const [summaryData, setSummaryData] = useState(null);
    const [error, setError] = useState(null);
    const [loadingCsv, setLoadingCsv] = useState(false);
    const [loadingForecasts, setLoadingForecasts] = useState(false);
    const [loadingTable, setLoadingTable] = useState(false);
    const [filtersExpanded, setFiltersExpanded] = useState(false);
    const [showActuals, setShowActuals] = useState(false);
    const [loadingActuals, setLoadingActuals] = useState(false);
    const [showCompare, setShowCompare] = useState(false);
    const previousShowActualsRef = useRef(showActuals);

    const getToken = () => localStorage.getItem("token");

    const handleFiltersToggle = () => {
        setFiltersExpanded(prev => !prev);
    };

    useEffect(() => {
        const storedForecast = JSON.parse(localStorage.getItem('selectedForecast'));
        const storedForecastPeriodId = localStorage.getItem('selectedForecastPeriodId');

        if (storedForecast?.forecastId) {
            setSelectedForecastId(storedForecast.forecastId);
        }

        if (storedForecastPeriodId && storedForecastPeriodId !== "null") {
            setSelectedForecastPeriodId(parseInt(storedForecastPeriodId, 10));
        }

        fetchForecastsAndPeriods();
    }, []);

    const fetchForecastsAndPeriods = async () => {
        try {
            setLoadingForecasts(true);
            const token = getToken();
            const response = await axiosInstance.get("/forecasts", {
                headers: { Authorization: `Bearer ${token}` },
            });
            setForecasts(response.data);
        } catch (error) {
            console.log(error);
        } finally {
            setLoadingForecasts(false);
        }
    };

    const fetchAccountSummary = useCallback(async (
        { includeActuals, showTableLoader, showActualsLoader } = { includeActuals: false, showTableLoader: true, showActualsLoader: false }
    ) => {
        if (!selectedForecastId || selectedForecastPeriodId === null) {
            if (showTableLoader) setLoadingTable(false);
            if (showActualsLoader) setLoadingActuals(false);
            return;
        }

        if (showTableLoader) setLoadingTable(true);
        if (showActualsLoader) setLoadingActuals(true);

        try {
            const params = {
                forecastPeriodId: selectedForecastPeriodId,
                customerId: selectedCustomerId,
                channelId: selectedChannelId,
                aggregationPeriod
            };

            if (selectedProductIds && Array.isArray(selectedProductIds) && selectedProductIds.length > 0) {
                params.productIds = selectedProductIds.join(",");
            }
            if (selectedBrands && Array.isArray(selectedBrands) && selectedBrands.length > 0) {
                params.brands = selectedBrands.join(",");
            }
            if (selectedProductGroups && Array.isArray(selectedProductGroups) && selectedProductGroups.length > 0) {
                params.productGroups = selectedProductGroups.join(",");
            }
            if (includeActuals) {
                params.includeActuals = true;
            }

            const response = await axiosInstance.get(`/forecasts/${selectedForecastId}/outputs-account`, { params });
            setSummaryData(response.data);
            setError(null);
        } catch (error) {
            console.error("Failed to fetch account summary:", error);
            setSummaryData(null);
            setError(error.response?.status === 404 ? "Account summary not found." : "An unexpected error occurred.");
        } finally {
            if (showTableLoader) setLoadingTable(false);
            if (showActualsLoader || includeActuals) {
                setLoadingActuals(false);
            }
        }
    }, [
        selectedForecastId,
        selectedForecastPeriodId,
        selectedCustomerId,
        selectedChannelId,
        selectedProductIds,
        selectedBrands,
        selectedProductGroups,
        aggregationPeriod
    ]);

    useEffect(() => {
        const includeActuals = showActuals;
        const toggledActuals = previousShowActualsRef.current !== includeActuals;
        previousShowActualsRef.current = includeActuals;

        if (toggledActuals && !includeActuals) {
            setLoadingActuals(false);
            return;
        }

        fetchAccountSummary({
            includeActuals,
            showTableLoader: !toggledActuals,
            showActualsLoader: toggledActuals && includeActuals
        });
    }, [fetchAccountSummary, showActuals]);

    const handleForecastSelected = (id) => {
        localStorage.setItem('selectedForecast', JSON.stringify({ forecastId: id }));
        setSelectedForecastId(id);
    };

    const handleCustomerSelected = (selection) => {
        setSelectedBrands([]);
        setSelectedProductGroups([]);
        setSelectedProductIds([]);
        
        if (selection.type === 'channel') {
            setSelectedChannelId(selection.id);
            setSelectedCustomerId(null);
        } else if (selection.type === 'clear') {
            setSelectedCustomerId(null);
            setSelectedChannelId(null);
        } else {
            setSelectedCustomerId(selection.id);
            setSelectedChannelId(null);
        }
    };

    const handleForecastPeriodSelected = (forecastPeriodId) => {
        localStorage.setItem("selectedForecastPeriodId", forecastPeriodId);
        setSelectedForecastPeriodId(forecastPeriodId);
    };

    const handleAggregationPeriodChange = (event, newValue) => {
        setAggregationPeriod(newValue);
    };

    const handleActualsToggle = (event) => {
        const checked = event.target.checked;
        if (checked) {
            setLoadingActuals(true);
        } else {
            setLoadingActuals(false);
            setShowCompare(false);
        }
        setShowActuals(checked);
    };

    const handleCompareToggle = (event) => {
        setShowCompare(event.target.checked);
    };

    const handleDownloadCsv = async () => {
        try {
            const params = {
                forecastPeriodId: selectedForecastPeriodId,
                channelId: selectedChannelId,
                customerId: selectedCustomerId
            };
            
            if (selectedProductIds !== null && Array.isArray(selectedProductIds) && selectedProductIds.length > 0) {
                params.productIds = selectedProductIds.join(",");
            }
            if (selectedBrands !== null && Array.isArray(selectedBrands) && selectedBrands.length > 0) {
                params.brands = selectedBrands.join(",");
            }
            if (selectedProductGroups !== null && Array.isArray(selectedProductGroups) && selectedProductGroups.length > 0) {
                params.productGroups = selectedProductGroups.join(",");
            }
            if (showActuals) {
                params.includeActuals = true;
            }
            const fileName = `OutputsAccount_${selectedForecastId}_${selectedForecastPeriodId}.csv`;

            await downloadCsv(`/forecasts/${selectedForecastId}/outputs-account-csv`, params, fileName, setLoadingCsv, setError);
        } catch (error) {
            console.error("Unexpected error during CSV download:", error);
        }
    };

    const selectedForecast = forecasts.find((f) => f.forecastId === selectedForecastId);

    return (
        <Box className="component-container">
            <Paper elevation={2} sx={{ p: 3, mb: 2 }}>
                <Grid container spacing={3} alignItems="center">
                    <Grid item xs={12} sm="auto">
                        {loadingForecasts ? (
                            <Skeleton variant="rectangular" width={200} height={56} />
                        ) : (
                            <ForecastDropdown
                                forecasts={forecasts}
                                selectedForecastId={selectedForecastId}
                                onForecastSelected={handleForecastSelected}
                                width={200}
                            />
                        )}
                    </Grid>
                    <Grid item xs={12} sm="auto">
                        {loadingForecasts ? (
                            <Skeleton variant="rectangular" width={200} height={56} />
                        ) : (
                            <ForecastPeriodDropdown
                                forecastPeriods={selectedForecast?.forecastPeriods || []}
                                selectedForecastPeriodId={selectedForecastPeriodId}
                                onForecastPeriodSelected={handleForecastPeriodSelected}
                                width={200}
                            />
                        )}
                    </Grid>
                    <Grid item xs={12} sm="auto">
                        {loadingForecasts ? (
                            <Skeleton variant="rectangular" width={200} height={56} />
                        ) : (
                            <CustomerDropdown onCustomerSelected={handleCustomerSelected} channelSelectable={true} />
                        )}
                    </Grid>
                    <Grid item xs={12} sm="auto">
                        {loadingForecasts ? (
                            <Skeleton variant="rectangular" width={150} height={56} />
                        ) : (
                            <ProductFiltersButton
                                filtersExpanded={filtersExpanded}
                                onToggle={handleFiltersToggle}
                                disabled={loadingForecasts}
                            />
                        )}
                    </Grid>
                    <Grid item xs={12} sm="auto">
                        {loadingForecasts ? (
                            <Skeleton variant="circular" width={32} height={32} />
                        ) : (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: (theme) => theme.spacing(2) }}>
                                <Tooltip title="Download CSV">
                                    <span>
                                        <IconButton onClick={handleDownloadCsv} aria-label="download csv" disabled={loadingCsv}>
                                            {loadingCsv ? <CircularProgress size={24} /> : <FileDownloadIcon />}
                                        </IconButton>
                                    </span>
                                </Tooltip>
                                <FormControlLabel
                                    control={
                                        <Switch
                                            size="small"
                                            checked={showActuals}
                                            onChange={handleActualsToggle}
                                            disabled={loadingForecasts || loadingTable}
                                            inputProps={{ 'aria-label': 'toggle actuals' }}
                                        />
                                    }
                                    label={
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                            Actuals
                                            {loadingActuals && <CircularProgress size={16} />}
                                        </Box>
                                    }
                                    labelPlacement="end"
                                />
                                {showActuals && !loadingActuals && (
                                    <FormControlLabel
                                        control={
                                            <Switch
                                                size="small"
                                                checked={showCompare}
                                                onChange={handleCompareToggle}
                                                disabled={loadingForecasts || loadingTable}
                                                inputProps={{ 'aria-label': 'toggle compare' }}
                                            />
                                        }
                                        label="Compare"
                                        labelPlacement="end"
                                    />
                                )}
                            </Box>
                        )}
                    </Grid>
                </Grid>
            </Paper>

            <ProductFilters
                selectedForecastId={selectedForecastId}
                selectedForecastPeriodId={selectedForecastPeriodId}
                selectedBrands={selectedBrands}
                setSelectedBrands={setSelectedBrands}
                selectedProductGroups={selectedProductGroups}
                setSelectedProductGroups={setSelectedProductGroups}
                selectedProductIds={selectedProductIds}
                setSelectedProductIds={setSelectedProductIds}
                filtersExpanded={filtersExpanded}
                onToggle={handleFiltersToggle}
                selectedCustomerId={selectedCustomerId}
                selectedChannelId={selectedChannelId}
            />

            {error ? (
                <div>{error}</div>
            ) : (
                <>
                    <Paper elevation={1} sx={{ mb: 2 }}>
                        <Tabs 
                            value={aggregationPeriod} 
                            onChange={handleAggregationPeriodChange}
                            sx={{ borderBottom: 1, borderColor: 'divider' }}
                        >
                            <Tab label="Monthly" value="monthly" />
                            <Tab label="Quarterly" value="quarterly" />
                            <Tab label="Weekly" value="weekly" />
                        </Tabs>
                    </Paper>
                    {loadingTable ? (
                        <Skeleton variant="rectangular" width="100%" height={400} />
                    ) : (
                        <OutputsAccountTable 
                            data={summaryData} 
                            startMonth={summaryData?.startMonth || 1} 
                            selectedForecast={selectedForecast}
                            aggregationPeriod={aggregationPeriod}
                            showActuals={showActuals}
                            loadingActuals={loadingActuals}
                            showCompare={showCompare}
                        />
                    )}
                </>
            )}
        </Box>
    );
};

export default ForecastOutputsAccount;
